'use client';

import React, { useId, useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { TbCheck as Check, TbMail as Mail } from 'react-icons/tb';
import PixelSquares from './PixelSquares';

// 'card' sits on the dark site background; 'compact' is the footer, which is light.
type Variant = 'card' | 'compact';
type Status = 'idle' | 'sending' | 'done' | 'already' | 'invalid' | 'error';

const copy = {
  en: {
    title: 'The nibras newsletter',
    lede: 'Practical articles on marketing, branding and search visibility, sent to your inbox. No spam, and you can unsubscribe in one click.',
    placeholder: 'Your email',
    submit: 'Subscribe',
    sending: 'Subscribing…',
    done: "You're subscribed! Check your inbox for our welcome email.",
    already: "You're already subscribed. Thanks for being with us!",
    invalid: 'Check the email address and try again.',
    error: "Couldn't subscribe right now. Please try again.",
    consent: 'By subscribing you agree to receive emails from nibras under our',
    privacy: 'Privacy Policy',
    batool: 'Want our new articles first?',
    batoolDone: "You're in! Welcome aboard",
    batoolAlready: "You're already with us!",
    batoolAlt: 'Batool, the nibras assistant, waving',
  },
  ar: {
    title: 'نشرة نبراس',
    lede: 'مقالات عملية عن التسويق والهوية البصرية والظهور في البحث، تصلك على بريدك. بلا إزعاج، وإلغاء الاشتراك بنقرة واحدة.',
    placeholder: 'بريدك الإلكتروني',
    submit: 'اشترك',
    sending: 'جارٍ الاشتراك…',
    done: 'تم اشتراكك! تفقّد بريدك، أرسلنا لك رسالة ترحيب.',
    already: 'أنت مشترك معنا من قبل. شكرًا لوجودك!',
    invalid: 'تأكد من البريد الإلكتروني وحاول مرة أخرى.',
    error: 'تعذّر الاشتراك الآن. حاول مرة أخرى.',
    consent: 'بالاشتراك توافق على استلام رسائل من نبراس وفق',
    privacy: 'سياسة الخصوصية',
    batool: 'تبي جديدنا يوصلك أول بأول؟',
    batoolDone: 'أهلًا فيك معنا!',
    batoolAlready: 'أنت معنا من قبل!',
    batoolAlt: 'بتول، مساعدة نبراس، تلوّح بيدها',
  },
};

export default function NewsletterSignup({
  locale,
  source,
  variant = 'card',
  className = '',
}: {
  locale: string;
  source: string;
  variant?: Variant;
  className?: string;
}) {
  const isAr = locale === 'ar';
  const t = isAr ? copy.ar : copy.en;
  const inputId = useId();
  const [email, setEmail] = useState('');
  const [website, setWebsite] = useState('');
  const [status, setStatus] = useState<Status>('idle');

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (status === 'sending') return;
    setStatus('sending');
    try {
      const response = await fetch('/api/newsletter', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, locale: isAr ? 'ar' : 'en', source, website }),
      });
      if (response.ok) {
        const result = await response.json().catch(() => ({}));
        setStatus(result.status === 'already' ? 'already' : 'done');
      } else {
        setStatus(response.status === 400 ? 'invalid' : 'error');
      }
    } catch {
      setStatus('error');
    }
  };

  const compact = variant === 'compact';
  const tone = compact
    ? {
        text: 'text-[#081821]',
        input: 'border-[#081821]/25 bg-white focus:border-[#081821]/60 focus:ring-[#081821]/15',
        fine: 'text-[#081821]/60 hover:text-[#081821]',
        ring: 'focus-visible:ring-[#081821]/60',
      }
    : {
        text: 'text-white',
        input: 'border-white bg-white shadow-lg shadow-black/10 focus:border-brand-cta focus:ring-brand-cta/30',
        fine: 'text-brand-text/50 hover:text-white',
        ring: 'focus-visible:ring-white/70',
      };
  const message = status === 'invalid' ? t.invalid : status === 'error' ? t.error : null;

  const form =
    status === 'done' || status === 'already' ? (
      <p role="status" className={`flex items-start gap-2 font-body text-base ${tone.text}`}>
        <Check className="mt-0.5 h-5 w-5 shrink-0 text-brand-cta" aria-hidden />
        {status === 'already' ? t.already : t.done}
      </p>
    ) : (
      <form onSubmit={onSubmit} noValidate className="space-y-3">
        <div className="flex flex-col gap-3 sm:flex-row">
          <label htmlFor={inputId} className="sr-only">
            {t.placeholder}
          </label>
          <div className="relative min-w-0 flex-1">
            <Mail aria-hidden className="pointer-events-none absolute start-4 top-1/2 h-5 w-5 -translate-y-1/2 text-[#081821]/45" />
            <input
              id={inputId}
              type="email"
              inputMode="email"
              autoComplete="email"
              required
              dir="ltr"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (status === 'invalid' || status === 'error') setStatus('idle');
              }}
              placeholder={t.placeholder}
              aria-invalid={status === 'invalid'}
              // dir="ltr" keeps the address readable, so padding is set by side,
              // not start/end, to clear the icon on the correct side.
              className={`w-full rounded-full border py-3.5 font-body text-base text-[#081821] placeholder-[#081821]/50 outline-none transition focus:ring-4 aria-[invalid=true]:border-brand-cta ${tone.input} ${
                isAr ? 'pr-12 pl-5 text-right placeholder:text-right' : 'pl-12 pr-5'
              }`}
            />
          </div>
          {/* Honeypot: hidden from people, filled by bots. */}
          <input
            type="text"
            name="website"
            tabIndex={-1}
            autoComplete="off"
            aria-hidden
            value={website}
            onChange={(e) => setWebsite(e.target.value)}
            className="hidden"
          />
          <button
            type="submit"
            disabled={status === 'sending'}
            className={`shrink-0 rounded-full bg-brand-cta px-7 py-3.5 font-heading font-bold text-white transition-colors hover:bg-brand-cta/90 disabled:opacity-70 focus-visible:outline-none focus-visible:ring-2 ${tone.ring}`}
          >
            {status === 'sending' ? t.sending : t.submit}
          </button>
        </div>
        {message && (
          <p role="alert" className="font-body text-sm text-brand-cta">
            {message}
          </p>
        )}
        <p className={`font-body text-xs leading-relaxed ${compact ? 'text-[#081821]/60' : 'text-brand-text/50'}`}>
          {t.consent}{' '}
          <Link href={`/${isAr ? 'ar' : 'en'}/privacy`} className={`underline underline-offset-2 ${tone.fine}`}>
            {t.privacy}
          </Link>
          .
        </p>
      </form>
    );

  if (compact) {
    return (
      <div className={`space-y-3 ${className}`} dir={isAr ? 'rtl' : 'ltr'}>
        <h3 className={`font-heading text-2xl ${tone.text}`}>{t.title}</h3>
        {form}
      </div>
    );
  }

  // Batool stands at the card's end edge and pops out above it, so the card
  // itself is not overflow-hidden; the decorative layer clips separately.
  return (
    <div dir={isAr ? 'rtl' : 'ltr'} className={`relative mt-16 md:mt-24 ${className}`}>
      <div className="pointer-events-none absolute inset-0 overflow-hidden rounded-[2.5rem] border border-white/10 bg-gradient-to-br from-brand-cta/[0.14] via-white/[0.03] to-brand-text/[0.08] shadow-xl">
        <PixelSquares pattern="steps" tone="orange" className="-bottom-6 start-6" opacityClassName="opacity-[0.12]" sizeClassName="w-40" />
        <PixelSquares pattern="scatter" tone="red" className="top-6 end-[30%]" opacityClassName="opacity-[0.15]" sizeClassName="w-28" />
      </div>

      <div className="relative z-10 grid md:grid-cols-[1fr_15rem] lg:grid-cols-[1fr_18rem]">
        <div className="space-y-5 p-8 pt-40 md:p-12">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-white">{t.title}</h2>
          <p className="max-w-lg font-body text-base leading-relaxed text-brand-text/75">{t.lede}</p>
          <div className="max-w-xl">{form}</div>
        </div>

        {/* Batool + speech bubble: above the text on mobile, at the end edge from md up */}
        <div className="pointer-events-none absolute inset-x-0 -top-14 flex h-52 justify-end pe-6 md:static md:h-auto md:pe-0">
          <div className="service-card-label-float relative h-full md:absolute md:bottom-0 md:end-4 md:h-[calc(100%+5rem)] lg:end-8">
            <Image
              src="/assets/ai/batool-wave-cutout.webp"
              alt={t.batoolAlt}
              width={479}
              height={640}
              sizes="(max-width: 768px) 160px, 260px"
              className="h-full w-auto object-contain object-bottom drop-shadow-2xl"
            />
            <p
              key={status === 'done' || status === 'already' ? status : 'hi'}
              className="absolute -top-2 end-[78%] w-max max-w-[11rem] rounded-2xl rounded-ee-sm bg-[#081821] px-3.5 py-2 font-body text-sm font-bold leading-snug text-white shadow-lg ring-1 ring-brand-text/40 motion-safe:animate-fade-in md:top-6"
            >
              {status === 'done' ? t.batoolDone : status === 'already' ? t.batoolAlready : t.batool}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
